import {
  degreesLat,
  degreesLong,
  eciToGeodetic,
  gstime,
  json2satrec,
  propagate,
  type SatRec,
} from 'satellite.js';

import { latLonAltToEcef } from '../lib/geo3d';
import type { Satellite, SatellitePositionRaw } from './types';

const satrecCache = new Map<string, SatRec>();

function getSatrec(sat: Satellite): SatRec | null {
  let rec = satrecCache.get(sat.noradId);
  if (!rec) {
    try {
      rec = json2satrec(sat.omm as Parameters<typeof json2satrec>[0]);
      satrecCache.set(sat.noradId, rec);
    } catch {
      return null;
    }
  }
  return rec;
}

export function propagateSatelliteRaw(sat: Satellite, date: Date): SatellitePositionRaw | null {
  const satrec = getSatrec(sat);
  if (!satrec) return null;
  const pv = propagate(satrec, date);
  if (!pv?.position) return null;
  const gmst = gstime(date);
  const gd = eciToGeodetic(pv.position, gmst);
  const lat = degreesLat(gd.latitude);
  const lon = degreesLong(gd.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const altKm = gd.height;
  let speedKmS = 0;
  if (pv.velocity) {
    const { x, y, z } = pv.velocity;
    speedKmS = Math.sqrt(x * x + y * y + z * z);
  }
  const ecef = latLonAltToEcef(lat, lon, altKm);
  return { noradId: sat.noradId, lat, lon, altKm, ecef, speedKmS };
}

export function propagateCatalogRaw(catalog: Satellite[], date: Date): SatellitePositionRaw[] {
  const out: SatellitePositionRaw[] = [];
  for (const sat of catalog) {
    const pos = propagateSatelliteRaw(sat, date);
    if (pos) out.push(pos);
  }
  return out;
}

export function propagateOrbitPoints(
  sat: Satellite,
  date: Date,
  samples = 120,
): Array<[number, number, number]> {
  const satrec = getSatrec(sat);
  if (!satrec) return [];
  const periodMin = sat.meanMotion > 0 ? 1440 / sat.meanMotion : 90;
  const stepMs = (periodMin * 60 * 1000) / samples;
  const points: Array<[number, number, number]> = [];
  for (let i = 0; i <= samples; i++) {
    const t = new Date(date.getTime() + i * stepMs);
    const pv = propagate(satrec, t);
    if (!pv?.position) continue;
    const gmst = gstime(t);
    const gd = eciToGeodetic(pv.position, gmst);
    const lat = degreesLat(gd.latitude);
    const lon = degreesLong(gd.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    points.push(latLonAltToEcef(lat, lon, gd.height));
  }
  return points;
}
